import { SITUACAO_DO_FORNECEDOR, degrauDe } from '../situacoes';
import { BarraDeLista, BuscaDaBarra, FiltrosAtivos } from '../BarraDeLista';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { data } from '../formato';
import { useSessao } from '../sessao';
import {
  Area, Aviso, Campo, Carregando, Dialogo, ErroDaApi, Selo, Vazio,
} from '../ui';

/**
 * Os provedores externos de calibração.
 *
 * ── O pedido, e a palavra ────────────────────────────────────────────
 *
 * «Os fornecedores de calibração devem ser homologados [talvez essa não
 *  seja a palavra certa] antes de poderem prestar serviço de calibração
 *  para a Testesolo» — Humberto, 01/09/2026.
 *
 * A palavra da norma é **avaliar e aprovar**: a 17025 §6.6 pede que o
 * laboratório defina critérios, avalie o provedor externo, aprove,
 * monitore o desempenho e reavalie periodicamente — guardando o registro
 * de tudo isso. «Homologar» é o que a indústria diz e se entende. A tela
 * usa as duas palavras de propósito: a da norma no botão, a da casa no
 * texto.
 *
 * ── Por que uma tela, e não um campo ────────────────────────────────
 *
 * Porque o que a norma pede é um REGISTRO, não uma escolha. Um campo de
 * texto «empresa» num certificado guarda um nome; esta tela guarda a
 * acreditação, o escopo aprovado, quem aprovou, quando, com que critério
 * e até quando vale. É a diferença entre dizer que se escolheu bem e
 * poder mostrar como.
 *
 * ── O que ela NÃO faz ───────────────────────────────────────────────
 *
 * Não recusa certificado. Quem recusa é o banco (`0114`): a porta de
 * cadastro não é a única — importação e carga entram pelo mesmo lugar.
 */

interface Fornecedor {
  id: string;
  razao_social: string; apelido: string | null; cnpj: string | null;
  acreditacao: string | null; acreditacao_ate: string | null;
  escopo: string[]; situacao: string; estado: string;
  criterios: string | null;
  avaliado_em: string | null; aprovado_em: string | null;
  aprovacao_ate: string | null;
  aprovado_por_nome: string | null; avaliado_por_nome: string | null;
  contato: string | null; telefone: string | null; email: string | null;
  observacoes: string | null; ativo: boolean;
  certificados: number; pode_prestar: boolean;
}

interface Grandeza { codigo: string; nome: string; unidade_usual: string | null }

/* A escada do fornecedor mora em `situacoes.ts` (P-UI-41, onda 351). */

export function Fornecedores() {
  const { pode } = useSessao();
  const [lista, setLista] = useState<Fornecedor[] | null>(null);
  const [grandezas, setGrandezas] = useState<Grandeza[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [novo, setNovo] = useState(false);
  const [aprovando, setAprovando] = useState<Fornecedor | null>(null);
  const [mudando, setMudando] = useState<Fornecedor | null>(null);
  /* A busca da barra do padrão (onda 273): a lista não tinha nenhuma. */
  const [busca, setBusca] = useState('');

  const carregar = useCallback(() => {
    api<Fornecedor[]>('/fornecedores').then(setLista).catch(setErro);
    api<Grandeza[]>('/equipamentos/grandezas')
      .then(setGrandezas).catch(() => setGrandezas([]));
  }, []);
  useEffect(carregar, [carregar]);

  const nomeDa = (c: string) =>
    grandezas.find((g) => g.codigo === c)?.nome ?? c;

  if (erro && !lista) return <ErroDaApi erro={erro} />;
  if (!lista) return <Carregando texto="Carregando os fornecedores" />;

  const cadastra = pode('cadastro.categoria');
  const aprova = pode('equipamento.aprovar_certificado');
  const semAprovacao = lista.filter((f) => !f.pode_prestar && f.ativo);
  const termo = busca.trim().toLowerCase();
  const visiveis = termo
    ? lista.filter((f) => [f.razao_social, f.cnpj, f.acreditacao, f.contato]
      .filter(Boolean).join(' ').toLowerCase().includes(termo))
    : lista;

  return (
    <div className="pagina">
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Fornecedores de calibração</h1>
          <p className="sub">
            Quem calibra os equipamentos da Testesolo. A 17025 pede que o
            laboratório <b>avalie e aprove</b> o provedor externo antes do
            serviço, defina o critério dessa escolha e reavalie de tempos em
            tempos (§6.6) — é o que a casa chama de homologar. Os que podem
            prestar primeiro.
          </p>
        </div>
        {cadastra && (
          <button type="button" className="botao" onClick={() => setNovo(true)}>
            + Fornecedor
          </button>
        )}
      </div>

      <ErroDaApi erro={erro} />

      {semAprovacao.length > 0 && (
        <Aviso tipo="atencao"
          titulo={`${semAprovacao.length} fornecedor(es) sem aprovação vigente`}>
          Enquanto não estiverem aprovados, o sistema recusa certificado
          emitido por eles — e a recusa é do banco, não da tela.
        </Aviso>
      )}

      <BarraDeLista rotulo="Filtros dos fornecedores">
        <BuscaDaBarra valor={busca} aoMudar={setBusca}
          placeholder="Razão social, CNPJ, acreditação ou contato" rotulo="Buscar fornecedor" />
      </BarraDeLista>
      <FiltrosAtivos
        itens={termo ? [{ chave: 'q', texto: `Busca: ${busca.trim()}`, tirar: () => setBusca('') }] : []}
        aoLimpar={() => setBusca('')}
        contagem={`${visiveis.length} de ${lista.length} ${lista.length === 1 ? 'fornecedor' : 'fornecedores'}`} />

      <div className="cartao">
        {visiveis.length === 0 && lista.length > 0
          ? <Vazio>Nenhum fornecedor com essa busca.</Vazio>
          : lista.length === 0
          ? (
            <Vazio>
              Nenhum fornecedor cadastrado. Os 116 certificados que entraram
              por carga não dizem quem calibrou — cadastrar os laboratórios
              aqui é o primeiro passo para fechar esse buraco.
            </Vazio>
          )
          : (
            <div className="rola">
              <table className="dados">
                <thead>
                  <tr>
                    <th>Fornecedor</th>
                    <th>Acreditação</th>
                    <th>Escopo aprovado</th>
                    <th>Aprovação</th>
                    <th className="num">Certificados</th>
                    <th>Situação</th>
                    {(aprova || cadastra) && <th>Ações</th>}
                  </tr>
                </thead>
                <tbody>
                  {visiveis.map((f) => {
                    const e = degrauDe(SITUACAO_DO_FORNECEDOR, f.estado);
                    return (
                      <tr key={f.id} className={f.pode_prestar ? undefined : 'linha-alerta'}>
                        <td>
                          <b>{f.razao_social}</b>
                          {f.cnpj && <div className="sub mono">{f.cnpj}</div>}
                          {f.contato && <div className="sub">{f.contato}</div>}
                        </td>
                        <td className="sub">
                          {f.acreditacao ?? <span className="nada">não declarada</span>}
                          {f.acreditacao_ate && (
                            <div className="sub">até {data(f.acreditacao_ate)}</div>
                          )}
                        </td>
                        <td>
                          {f.escopo.length === 0
                            ? <span className="nada">—</span>
                            : (
                              <span className="escopo">
                                {f.escopo.map((c) => (
                                  <Selo key={c} tom="neutro">{nomeDa(c)}</Selo>
                                ))}
                              </span>
                            )}
                        </td>
                        <td className="sub">
                          {f.aprovado_em
                            ? (
                              <>
                                {data(f.aprovado_em)}
                                {f.aprovado_por_nome && ` · ${f.aprovado_por_nome}`}
                                {f.aprovacao_ate && (
                                  <div className="sub">
                                    reavaliar até {data(f.aprovacao_ate)}
                                  </div>
                                )}
                              </>
                            )
                            : <span className="nada">nunca aprovado</span>}
                        </td>
                        <td className="num mono">{f.certificados}</td>
                        <td><Selo tom={e.tom}>{e.texto}</Selo></td>
                        {(aprova || cadastra) && (
                          <td>
                            {aprova && (
                              <button type="button" className="botao discreto"
                                onClick={() => setAprovando(f)}>
                                {f.situacao === 'aprovado' ? 'Reavaliar' : 'Aprovar'}
                              </button>
                            )}
                            {aprova && f.situacao === 'aprovado' && (
                              <button type="button" className="botao discreto"
                                onClick={() => setMudando(f)}>
                                Suspender
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        <p className="rodape-regra">
          A situação é conferida <b>na data da calibração</b>, e não hoje: um
          fornecedor suspenso em 2027 estava aprovado quando calibrou em
          2026, e aquele certificado continua valendo. Reescrever a história
          pela situação de hoje é o oposto do que o §11 pede.
        </p>
      </div>

      {novo && (
        <FormFornecedor
          grandezas={grandezas}
          aoFechar={() => setNovo(false)}
          aoPronto={() => { setNovo(false); carregar(); }}
        />
      )}
      {aprovando && (
        <FormAprovar
          f={aprovando}
          grandezas={grandezas}
          aoFechar={() => setAprovando(null)}
          aoPronto={() => { setAprovando(null); carregar(); }}
        />
      )}
      {mudando && (
        <FormSituacao
          f={mudando}
          aoFechar={() => setMudando(null)}
          aoPronto={() => { setMudando(null); carregar(); }}
        />
      )}
    </div>
  );
}


function FormFornecedor({ grandezas, aoFechar, aoPronto }: {
  grandezas: Grandeza[]; aoFechar(): void; aoPronto(): void;
}) {
  const [v, setV] = useState({
    razaoSocial: '', cnpj: '', acreditacao: '', acreditacaoAte: '',
    contato: '', telefone: '', email: '', observacoes: '',
  });
  const [escopo, setEscopo] = useState<Set<string>>(new Set());
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);

  const campo = (k: keyof typeof v) => ({
    value: v[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setV({ ...v, [k]: e.target.value }),
  });

  async function enviar() {
    setErro(null); setSalvando(true);
    try {
      await api('/fornecedores', {
        metodo: 'POST',
        corpo: {
          razaoSocial: v.razaoSocial.trim(),
          cnpj: v.cnpj.trim() || undefined,
          acreditacao: v.acreditacao.trim() || undefined,
          acreditacaoAte: v.acreditacaoAte || undefined,
          escopo: [...escopo],
          contato: v.contato.trim() || undefined,
          telefone: v.telefone.trim() || undefined,
          email: v.email.trim() || undefined,
          observacoes: v.observacoes.trim() || undefined,
        },
      });
      aoPronto();
    } catch (e) { setErro(e); setSalvando(false); }
  }

  return (
    <Dialogo aberto titulo="Cadastrar fornecedor de calibração" aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      <Aviso tipo="info" titulo="Nasce em avaliação, nunca aprovado.">
        Se o cadastro já nascesse aprovado, a avaliação viraria um campo que
        alguém preenche depois — e a §6.6 pede o contrário: avaliar antes de
        contratar o serviço.
      </Aviso>

      <Campo rotulo="Razão social" required maxLength={200} {...campo('razaoSocial')} />
      <div className="linha">
        <Campo rotulo="CNPJ" maxLength={20} {...campo('cnpj')} />
        <Campo rotulo="Acreditação" placeholder="CAL 0123" maxLength={120}
          dica="O número, e não «sim» — é ele que se confere no Inmetro."
          {...campo('acreditacao')} />
        <Campo rotulo="Acreditação válida até" type="date"
          {...campo('acreditacaoAte')} />
      </div>

      <fieldset className="lista-perfis">
        <legend>Grandezas que ele calibra</legend>
        {grandezas.map((g) => (
          <label key={g.codigo}>
            <input
              type="checkbox"
              checked={escopo.has(g.codigo)}
              onChange={() => setEscopo((s) => {
                const n = new Set(s);
                if (n.has(g.codigo)) n.delete(g.codigo); else n.add(g.codigo);
                return n;
              })}
            />
            {' '}{g.nome}
          </label>
        ))}
      </fieldset>

      <div className="linha">
        <Campo rotulo="Contato" maxLength={120} {...campo('contato')} />
        <Campo rotulo="Telefone" maxLength={40} {...campo('telefone')} />
        <Campo rotulo="E-mail" maxLength={120} {...campo('email')} />
      </div>
      <Area rotulo="Observações" rows={2} {...campo('observacoes')} />

      <div className="rodape-dialogo">
        <button type="button" className="botao"
          disabled={v.razaoSocial.trim().length < 3 || salvando}
          onClick={() => { void enviar(); }}>
          {salvando ? 'Cadastrando…' : 'Cadastrar'}
        </button>
        <button type="button" className="botao secundario" onClick={aoFechar}>
          Cancelar
        </button>
      </div>
    </Dialogo>
  );
}


function FormAprovar({ f, grandezas, aoFechar, aoPronto }: {
  f: Fornecedor; grandezas: Grandeza[]; aoFechar(): void; aoPronto(): void;
}) {
  const [criterios, setCriterios] = useState(f.criterios ?? '');
  const [ate, setAte] = useState(f.aprovacao_ate?.slice(0, 10) ?? '');
  const [escopo, setEscopo] = useState<Set<string>>(new Set(f.escopo));
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);

  async function enviar() {
    setErro(null); setSalvando(true);
    try {
      await api(`/fornecedores/${f.id}/aprovar`, {
        metodo: 'POST',
        corpo: {
          criterios: criterios.trim(),
          escopo: [...escopo],
          aprovacaoAte: ate || undefined,
        },
      });
      aoPronto();
    } catch (e) { setErro(e); setSalvando(false); }
  }

  return (
    <Dialogo aberto titulo={`Aprovar ${f.razao_social}`} aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      <p className="sub">
        Fica registrado quem aprovou, quando, com que critério e até quando
        vale. Os quatro são o registro que a §6.6 pede — sem eles,
        «aprovado» é um estado sem dono.
      </p>

      <fieldset className="lista-perfis">
        <legend>Aprovado para calibrar</legend>
        {grandezas.map((g) => (
          <label key={g.codigo}>
            <input
              type="checkbox"
              checked={escopo.has(g.codigo)}
              onChange={() => setEscopo((s) => {
                const n = new Set(s);
                if (n.has(g.codigo)) n.delete(g.codigo); else n.add(g.codigo);
                return n;
              })}
            />
            {' '}{g.nome}
          </label>
        ))}
      </fieldset>

      <Area
        rotulo="Critério da avaliação"
        rows={4}
        value={criterios}
        onChange={(e) => setCriterios(e.target.value)}
        dica="Acreditação, escopo, incerteza declarada, histórico de serviço.
              Quem auditar vai ler isto."
      />

      <Campo
        rotulo="Reavaliar até"
        type="date"
        value={ate}
        onChange={(e) => setAte(e.currentTarget.value)}
        dica="Opcional. A §6.6 pede reavaliação periódica; depois desta data
              o sistema recusa certificado novo dele."
      />

      <div className="rodape-dialogo">
        <button type="button" className="botao"
          disabled={criterios.trim().length < 20 || escopo.size === 0 || salvando}
          onClick={() => { void enviar(); }}>
          {salvando ? 'Aprovando…' : 'Aprovar'}
        </button>
        <button type="button" className="botao secundario" onClick={aoFechar}>
          Cancelar
        </button>
      </div>
    </Dialogo>
  );
}


function FormSituacao({ f, aoFechar, aoPronto }: {
  f: Fornecedor; aoFechar(): void; aoPronto(): void;
}) {
  const [situacao, setSituacao] = useState('suspenso');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);

  async function enviar() {
    setErro(null); setSalvando(true);
    try {
      await api(`/fornecedores/${f.id}/situacao`, {
        metodo: 'POST',
        corpo: { situacao, motivo: motivo.trim() },
      });
      aoPronto();
    } catch (e) { setErro(e); setSalvando(false); }
  }

  return (
    <Dialogo aberto titulo={`Mudar a situação de ${f.razao_social}`}
      aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      <Aviso tipo="info" titulo="Os certificados que ele já emitiu continuam valendo.">
        A situação é conferida na data da calibração. Suspender hoje impede
        certificado novo; não apaga o serviço que ele prestou quando estava
        aprovado.
      </Aviso>

      <label className="campo">
        <span className="rotulo">Nova situação</span>
        <select value={situacao} onChange={(e) => setSituacao(e.target.value)}>
          <option value="suspenso">Suspenso</option>
          <option value="em_avaliacao">Voltar para avaliação</option>
          <option value="reprovado">Reprovado</option>
        </select>
      </label>

      <Area rotulo="Motivo" rows={3} value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        dica="O certificado de amanhã depende disto — diga o que mudou." />

      <div className="rodape-dialogo">
        <button type="button" className="botao"
          disabled={motivo.trim().length < 10 || salvando}
          onClick={() => { void enviar(); }}>
          {salvando ? 'Gravando…' : 'Gravar'}
        </button>
        <button type="button" className="botao secundario" onClick={aoFechar}>
          Cancelar
        </button>
      </div>
    </Dialogo>
  );
}
